import React, { useState, useEffect } from "react";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { Box, Slider, Tooltip } from "@material-ui/core";
import useStyles from "../common/Styles";
import { useSelector } from "react-redux";
import { toolbarHeight } from "components/app/Styles";
import { filterContainerHeight, chartTitleHeight } from "../common/Styles";
import { getWindowSize } from "../common/utils";
import { createMuiTheme, ThemeProvider } from "@material-ui/core/styles";
import useGetViewPortDimensions from "hooks/useGetViewPortDimensions";

const theme = createMuiTheme({
  direction: "rtl",
});

const valueLabelFormat = (value) => {
  // Handles the case when the value has already been converted to the string format
  if (typeof value !== "number") {
    return value;
  }
  return `${value.toLocaleString("en-US")} acre-feet`;
};

const ValueLabelComponent = (props) => {
  const classes = useStyles();
  const { children, value } = props;
  const title = valueLabelFormat(value);
  return (
    <Tooltip
      placement="top"
      arrow
      title={title}
      open={true}
      classes={{
        tooltip: classes.cityTooltipPopper,
      }}
    >
      {children}
    </Tooltip>
  );
};

function CitiesStackBar() {
  const classes = useStyles();
  const { city_metrics, city_water_available, scenario, city_selected } =
    useSelector((state) => state.water_user);
  const { isMobile } = useGetViewPortDimensions();

  const [windowSize, setWindowSize] = useState(getWindowSize());

  let maxForColumn =
    city_metrics.tap_colorado_num +
    city_metrics.turf_colorado_num +
    city_metrics.aquifer_recharge_colorado_num +
    1000;
  const [waterAvailable, setWaterAvailable] = useState(maxForColumn);
  const [sliderValue, setSliderValue] = useState(100);

  //gets windowSize to set width of barchart
  useEffect(() => {
    const handleWindowResize = () => {
      setWindowSize(getWindowSize());
    };
    window.addEventListener("resize", handleWindowResize);
    return () => {
      window.removeEventListener("resize", handleWindowResize);
    };
  }, []);

  const options = {
    chart: {
      type: "column",
      // plotBackgroundColor: "#97B0DC",
      // plotBackgroundColor: "#E5E9EE",
      backgroundColor: "#E5E9EE",
      width: isMobile ? 380 : windowSize.innerWidth - 500,
      height: isMobile ? 500 :
        windowSize.innerHeight -
        toolbarHeight -
        filterContainerHeight -
        chartTitleHeight -
        75 -
        //slider height
        66,
    },
    title: {
      text: undefined,
    },
    series: [
      {
        name: "Colorado River Available",
        color: "#97B0DC",
      },
      {
        name: "Aquifer Recharge",
        data: [city_metrics.aquifer_recharge_colorado_num],
        color: "#2352D0",
      },
      {
        name: "Turf",
        data: [city_metrics.turf_colorado_num],
        color: "#F3C745",
      },
      {
        // name: "Delivered through Surface Water Treatment Plant",
        name: "Tap Water",
        data: [city_metrics.tap_colorado_num],
        color: "#D88C31",
      },
    ],
    plotOptions: {
      column: {
        stacking: "normal",
        states: {
          hover: {
            enabled: false,
          },
          inactive: {
            opacity: 1,
          },
        },
        borderWidth: 0,
        events: {
          legendItemClick: function () {
            return false; // <== returning false will cancel the default action
          },
        },
      },
      series: {
        pointWidth: 200,
      },
    },
    credits: {
      enabled: false,
    },
    xAxis: {
      categories: ["Current Water Delivered (acre-feet)"],
      labels: {
        style: {
          fontFamily: "Arial",
          fontWeight: "bold",
          fontSize: "16px",
        },
      },
    },
    yAxis: {
      // min: 0,
      max: maxForColumn,

      // tickAmount: 8,
      title: {
        text: "Colorado River water available to city (acre-feet)",
        margin: 40,
        style: {
          fontFamily: "Arial",
          fontWeight: "bold",
          fontSize: "16px",
        },
      },
      labels: {
        style: {
          fontFamily: "Arial",
          fontWeight: "bold",
          fontSize: "14px",
        },
        formatter: function () {
          if (this.value >= 1000000) {
            return this.value / 1000000 + "M";
          } else if (this.value >= 1000) {
            return this.value / 1000 + "K";
          } else {
            return this.value;
          }
        },
      },
      gridLineWidth: 1,
      gridLineColor: "#C4C4C4",
      plotBands: [
        {
          from: 0,
          to: waterAvailable,
          color: "#97B0DC",
        },
      ],
    },
    tooltip: {
      enabled: false,
    },
    legend: {
      reversed: true,
      title: {
        // text: "<span style='font-size:12px; color:#666; font-weight:normal font-family:Arial'>Legend</span>",
        // style: {
        //   fontStyle: "normal",
        // },
      },
      itemStyle: {
        color: "#333333",
        fontSize: "12px",
        fontWeight: "normal",
        fontFamily: "Arial",
      },
      align: "center",
      x: 50,
      backgroundColor: "#FFFFFF",
      borderColor: "#E0E0E0",
      borderWidth: 1,
      borderRadius: 15,
      padding: 10,
      itemMarginTop: 5,
      itemMarginBottom: 5,
      shadow: {
        offsetX: -1,
        offsetY: 3,
        color: "#00000029",
        width: 4,
      },
    },
  };

  const marks = [
    {
      value: 0,
      scaledValue: 800000,
      label: "Tier 3",
    },

    {
      value: 25,
      scaledValue: 850000,
      label: "Tier 2b",
    },

    {
      value: 50,
      scaledValue: 900000,
      label: "Tier 2a",
    },

    {
      value: 75,
      scaledValue: 1000000,
      label: "Tier 1",
    },
    {
      value: 100,
      scaledValue: 1300000,
      label: "Tier 0",
    },
  ];
  const marksSevere = [
    {
      value: 0,
      scaledValue: 300000,
      label: "300K",
    },

    {
      value: 25,
      scaledValue: 400000,
      label: "400K",
    },

    {
      value: 50,
      scaledValue: 500000,
      label: "500K",
    },

    {
      value: 75,
      scaledValue: 600000,
      label: "600K",
    },
    {
      value: 100,
      scaledValue: 700000,
      label: "700K",
    },
  ];

  const scale = (value) => {
    const previousMarkIndex = Math.floor(value / 25);
    const previousMark =
      scenario === 1
        ? marks[previousMarkIndex]
        : marksSevere[previousMarkIndex];
    const remainder = value % 25;
    if (remainder === 0 && previousMark !== undefined) {
      return previousMark.scaledValue;
    }
  };

  const handleSliderChange = (e, value) => {
    if (city_water_available[scale(value)]) {
      setWaterAvailable(city_water_available[scale(value)]["total_available"]);
    }
    setSliderValue(value);
  };

  const cityWater = () => {
    if (city_water_available[scenario === "DCP_Cuts" ? "1300000" : "900000"]) {
      maxForColumn =
        city_water_available[scenario === "DCP_Cuts" ? "1300000" : "900000"][
          "total_available"
        ];
      if (city_water_available[scale(sliderValue)]) {
        setWaterAvailable(
          city_water_available[scale(sliderValue)]["total_available"]
        );
      }
    }
  }
  useEffect(cityWater, [city_water_available, city_selected, scenario]);

  const setCityWater = () => {
    if (city_water_available[scale(sliderValue)]) {
      setWaterAvailable(
        city_water_available[scale(sliderValue)]["total_available"]
      );
    }
  }
  useEffect(setCityWater, [city_water_available]);

  // console.log("i am the slider value:", scale(sliderValue));
  // console.log("i am the water available:", waterAvailable);
  return (
    <Box>
      <Box className={classes.sliderContainer}>
        <ThemeProvider theme={theme}>
          <Slider
            // defaultValue={100}
            value={sliderValue}
            step={25}
            marks={scenario === 1 ? marks : marksSevere}
            scale={scale}
            min={0}
            max={100}
            classes={{
              root: classes.citySlider,
              rail: classes.citySliderRail,
              trackInverted: classes.citySliderTrackInverted,
              track: classes.citySliderTrack,
              thumb: classes.citySliderThumb,
              mark: classes.citySliderMark,
              markLabel: classes.cityMarkLabel,
            }}
            onChange={handleSliderChange}
            // track="inverted"
            valueLabelDisplay="on"
            getAriaValueText={valueLabelFormat}
            valueLabelFormat={valueLabelFormat}
            ValueLabelComponent={ValueLabelComponent}
          />
        </ThemeProvider>
      </Box>
      <Box className={classes.chartContainer}>
        <HighchartsReact
          highcharts={Highcharts}
          options={options}
          // containerProps={{ style: { width: "80%" } }}
        />
      </Box>
    </Box>
  );
}

export default CitiesStackBar;
